/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Box, Typography, styled } from '@mui/material';
import React from 'react';
import { useSelector } from 'react-redux';
import ContactCard from '../components/ContactCard';
import YesNoAction from '../components/YesNoAction';
import { selectContactInfo } from '../features/azure-b2c/storage';
import { TestIds } from '../testIds';
import { Capacitor } from '@capacitor/core';
import { TDictionary } from '../util/types';
import { BlockingPage } from './BlockingPages';

import AppStoreSolid from '../assets/icons/platform/AppStoreSolid.svg';
import PlayStoreSolid from '../assets/icons/platform/PlayStore.svg';

const SVGButton = styled('img')`
  cursor: pointer;
`;

export const VersionMismatch: React.FC<{ expected: string; current: string }> = ({
  expected,
  current,
}) => {
  const relationshipOwner = useSelector(selectContactInfo);

  const config: TDictionary<{ label: string; onClick: () => void } | undefined> = {
    web: {
      label: 'Refresh',
      onClick: () => {
        // eslint-disable-next-line no-self-assign
        window.location = window.location;
      },
    },
    ios: undefined,
    android: undefined,
  };

  const renderButton = () => {
    if (Capacitor.getPlatform() === 'ios') {
      return (
        <SVGButton
          src={AppStoreSolid}
          onClick={() => {
            window.open(process.env.REACT_APP_IOS_APPSTORE_URL, '_blank');
          }}
          alt='Get it on the App Store'
          height='60px'
        ></SVGButton>
      );
    } else if (Capacitor.getPlatform() === 'android') {
      return (
        <SVGButton
          src={PlayStoreSolid}
          onClick={() => {
            window.open(process.env.REACT_APP_GOOGLE_PLAYSTORE_URL, '_blank');
          }}
          alt='Get it on the Play Store'
          height='60px'
        ></SVGButton>
      );
    }
  };

  return (
    <BlockingPage data-testid={TestIds.pages.versionMismatch}>
      <Box>
        <YesNoAction
          display='FarmlandsPRO Update'
          subheading={
            <Typography variant='h4' sx={{ mt: 4, mb: 2 }}>
              New version {expected} available!
            </Typography>
          }
          body={
            <>
              <Typography variant='body2' gutterBottom sx={{ mb: 2 }}>
                Your current version is <b>{current}</b> <br />
                {Capacitor.getPlatform() === 'web'
                  ? 'Hit Refresh to get the latest updates'
                  : 'Please head to the app store to update to the recommended version'}
              </Typography>
              {renderButton()}
            </>
          }
          optionA={config[Capacitor.getPlatform()]}
        />
        <ContactCard
          sx={{ mt: 4 }}
          title='If you have any questions please contact your Farmlands representative.'
          data-testid={TestIds.components.contactCard.card}
          {...relationshipOwner}
        />
      </Box>
    </BlockingPage>
  );
};
